import React, { type FC, type PropsWithChildren } from 'react';

import kebabCase from 'lodash/kebabCase';
import { getColor } from '@lucid/theme';

import type { Lucid } from '../../../types/types';
import Box from '../../box/box';
import Heading from '../../heading/heading';
import Link from '../../link/link';
import Text from '../../text/text';
import type { StatGridProps } from '../stat-grid';

const MultiRowStatGrid: FC<PropsWithChildren<StatGridProps>> = ({
  headingHighlightColor,
  heading,
  link,
  items,
  languageKey = 'en',
  statColor,
  text,
}) => {
  const getFontSize = (shortCode: Lucid.I18n.LanguagesShortCode) => {
    const fs40Arr = ['ja', 'de', 'pt', 'sv'];
    const fs32Arr = ['ru', 'it', 'ko', 'pl', 'es'];

    if (fs40Arr.includes(shortCode)) {
      return '40px';
    }

    if (fs32Arr.includes(shortCode)) {
      return '32px';
    }

    return '80px';
  };

  return (
    <>
      <Box css={{ label: 'statGridDivided', display: 'flex', flexDirection: 'column' }} data-testid="stat-grid-divided">
        {heading && (
          <Heading
            as="h2"
            css={{
              maxWidth: '910px',
            }}
            highlightTextColor={headingHighlightColor}
            text={heading}
            variant="hero2XL700"
          />
        )}
        {text && (
          <Text
            css={theme => ({
              maxWidth: '650px',
              marginBottom: theme.space.s40,
            })}
            variant="paragraphL400"
          >
            {text}
          </Text>
        )}
        {link && (
          <Link
            css={theme => ({ fontSize: theme.fontSizes.fs20 })}
            href={link.final_url}
            iconAnimation="navigational"
            variant="primaryAlternate"
          >
            {link?.default_link_text}
          </Link>
        )}
      </Box>
      <Box
        css={theme => ({
          display: 'grid',
          marginTop: theme.space.s64,
          gridTemplateColumns: '260px',
          gap: theme.space.s24,
          placeItems: 'center',
          justifyContent: 'space-between',
          [theme.breakpoints.bp500.min]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },
          [theme.breakpoints.bp1024.min]: {
            gridTemplateRows: '1fr 4em 1fr 4em',
            gridColumnGap: '12px',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 260px))',
          },
        })}
      >
        {items?.map(({ heading, text }, i) => (
          <Box
            key={`stat-grid-item-divided-${kebabCase(heading || text)}`}
            css={theme => ({
              width: '100%',
              padding: `${theme.space.s40}px 0`,
              position: 'relative',
              '&::before': {
                content: '""',
                width: '1px',
                height: '100%',
                position: 'absolute',
                right: '-7px',
                top: 0,
                left: 0,
                background: theme.colors.coolGrey[100],
              },
              [theme.breakpoints.bp1024.min]: {
                '&, hgroup': {
                  display: 'grid',
                  gridTemplateRows: '1fr',
                  gridTemplateColumns: '260px',
                  gridRow: i < items.length / 2 ? '1 / 3' : '3 / 5',
                  position: 'relative',
                  gap: 0,
                },
                '&:last-of-type::after': {
                  height: 0,
                },
              },
            })}
          >
            <hgroup css={{ marginLeft: '16px', textWrap: 'wrap' }}>
              {heading && (
                <Heading
                  as="h3"
                  css={theme => ({
                    color: getColor({ color: statColor, theme }),
                    fontWeight: theme.fontWeights.fw300,
                    lineHeight: theme.lineHeights.lh1,
                    fontSize: getFontSize(languageKey),
                  })}
                  variant="headingM"
                >
                  {heading}
                </Heading>
              )}
              {text && (
                <Text
                  aria-level={3}
                  css={theme => ({
                    marginBottom: theme.space.s0,
                  })}
                  role="heading"
                  variant="headingL"
                >
                  {text}
                </Text>
              )}
            </hgroup>
          </Box>
        ))}
      </Box>
    </>
  );
};

export { MultiRowStatGrid };
export default MultiRowStatGrid;
